<template>
  <card-container
    :id="pageId"
    :disabled="!isFamilyPhoneNumberValid || !isEmailValid"
    hide-reset
    @submit="submitForm"
  >
    <template #title>
      Family Member Contact Information
    </template>

    <template #content>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label">First Name*</label>
        <div class="col-sm-3 mobile-pb-10">
          <sanitized-input
            id="familyFirstName"
            v-model="familyInfo.familyFirstName"
            placeholder="John"
            required
          />
        </div>

        <label class="col-sm-2 control-label">Last Name*</label>
        <div class="col-sm-3">
          <sanitized-input
            id="familyLastName"
            v-model="familyInfo.familyLastName"
            placeholder="Doe"
            required
          />
        </div>
      </div>

      <div class="row">
        <label class="col-sm-3 control-label pt-10">Phone Number*</label>
        <phone-number-input
          id="family-phone"
          v-model="familyInfo.familyPhoneNumber"
          class="col-sm-3 mobile-pb-10"
          :page-id="pageId"
          :required="false"
        />

        <label class="col-sm-2 control-label pt-10">Family Email</label>
        <div class="col-sm-3">
          <email-input
            v-model="familyInfo.familyEmail"
            @update-validation="updateEmailValidation"
          />
          <el-checkbox
            id="isAskedForLovedOneEmail"
            v-model="familyInfo.isAskedForLovedOneEmail"
            class="pt-5"
          >
            I have asked for the loved one's e-mail and the loved one does not have one
          </el-checkbox>
        </div>
      </div>

      <div class="row align-items-center">
        <label class="col-sm-3 control-label">Relationship*</label>
        <div class="col-sm-3">
          <dropdown-list
            v-model="familyInfo.familyRelation"
            :options="familyRelationList"
            required
            filterable
          />
        </div>

        <label
          v-if="otherReasonFieldShow"
          class="col-sm-2 control-label mobile-pt-10"
        >
          Please Specify*
        </label>
        <div
          v-if="otherReasonFieldShow"
          class="col-sm-3"
        >
          <sanitized-input
            id="otherRelationship"
            v-model="familyInfo.otherRelationship"
            required
          />
        </div>
      </div>

      <div class="row align-items-center">
        <div class="col-sm-5 offset-sm-3">
          <el-checkbox
            id="isFamilyNoSms"
            v-model="familyInfo.isFamilyNoSms"
          >
            Do Not Send safety & usage updates
          </el-checkbox>
        </div>
      </div>

      <div
        v-if="isFamilyInfoRequired"
        class="text-center pb-2"
      >
        <hr>
        <p class="error-message">
          A user with poor memory requires a family member phone number contact for emergencies.
        </p>
      </div>
    </template>

    <template #submit-button>
      {{ familyInfo.familyId ? 'Update Family Info' : 'Add Family Info' }}
    </template>

    <template #additional-buttons>
      <p-button
        type="yellow"
        round
        :disabled="!familyInfo.familyId"
        @click="removeLovedOne"
      >
        Remove Family Info
      </p-button>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch, onMounted } from 'vue';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useSwalDialog } from '@/use/swalDialog';
import { useCustomer } from '@/use/customer';
import { useEventListener } from '@/use/eventListener';
import { familyRelationList } from '@/constants/lovedOne';
import { LovedOneInput } from '@/models/lovedOne';
import { lovedOneService } from '@/services/lovedOne';

const pageId: string = 'create-update-family-member';

const emit = defineEmits(['change-family', 'goto-next-step']);

const { showSuccessPopupDialog, showErrorPopupDialog, showWarningPopupDialog } = useSwalDialog();
const { newCustomerInfo, setNewCustomerInfo } = useCustomer();

const familyInfo: Ref<any> = ref({
  familyId: null,
  familyFirstName: '',
  familyLastName: '',
  familyPhoneNumber: '',
  isFamilyNoSms: true,
  familyEmail: '',
  familyRelation: '',
  otherRelationship: '',
  isAskedForLovedOneEmail: false,
});
const isEmailValid = ref(true);

const isFamilyPhoneNumberValid = computed(() => util.isPhoneNumberValid(familyInfo.value.familyPhoneNumber));
const otherReasonFieldShow = computed(() => familyInfo.value.familyRelation === 'Other');
const isFamilyInfoRequired = computed(() => newCustomerInfo.value.isPoorMemory && !familyInfo.value.familyId);

const updateEmailValidation = (value: boolean) => {
  isEmailValid.value = value;
};

const removeLovedOne = async () => {
  if (!familyInfo.value.familyId) {
    return;
  }

  const loading = ElLoading.service({
    lock: true,
    text: 'Removing Family Member...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    await lovedOneService.deleteLovedOne({
      lovedOneId: familyInfo.value.familyId,
      callerId: newCustomerInfo.value.id,
    });

    familyInfo.value = {
      familyId: null,
      familyFirstName: '',
      familyLastName: '',
      familyPhoneNumber: '',
      isFamilyNoSms: true,
      familyEmail: '',
      familyRelation: '',
      otherRelationship: '',
    };

    showSuccessPopupDialog({
      text: 'The family info has been removed successfully.',
    });

    setNewCustomerInfo({
      ...newCustomerInfo.value,
      ...familyInfo.value,
    });

    emit('change-family', false);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  loading.close();
};

const createLovedOne = async (data: LovedOneInput) => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Adding Family Member...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });

  try {
    const lovedOne = await lovedOneService.addLovedOne({
      callerId: newCustomerInfo.value.id,
      data,
    });

    showSuccessPopupDialog({
      text: 'The family info has been created successfully.',
    });

    setNewCustomerInfo({
      ...newCustomerInfo.value,
      ...familyInfo.value,
      familyId: lovedOne.id,
    });

    familyInfo.value.familyId = lovedOne.id;

    emit('change-family', false);
    emit('goto-next-step');
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }

  loading.close();
};

const updateLovedOne = async (data: LovedOneInput) => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Updating Family Member...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });
  try {
    await lovedOneService.updateLovedOne({
      id: familyInfo.value.familyId,
      data,
    });

    await showSuccessPopupDialog({
      text: 'The family info has been updated successfully.',
    });
    setNewCustomerInfo({
      ...newCustomerInfo.value,
      ...familyInfo.value,
    });

    emit('change-family', false);
    emit('goto-next-step');
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    await showErrorPopupDialog({
      text: errorText,
    });
  }
  loading.close();
};

const submitForm = async () => {
  const postData = {
    prefix: '',
    firstName: familyInfo.value.familyFirstName,
    lastName: familyInfo.value.familyLastName,
    phoneNumber: familyInfo.value.familyPhoneNumber,
    transactionalSms: !familyInfo.value.isFamilyNoSms,
    email: familyInfo.value.familyEmail,
    relationship: otherReasonFieldShow.value ? familyInfo.value.otherRelationship : familyInfo.value.familyRelation,
  } as LovedOneInput;

  if (!postData.email && !familyInfo.value.isAskedForLovedOneEmail) {
    await showWarningPopupDialog({
      text: 'Please either ask the customer for loved one\'s email or select the "I have asked for the loved one\'s e-mail and the loved one does not have one" checkbox',
    });

    return;
  }

  if (postData.email && postData.email === familyInfo.value.email) {
    await showErrorPopupDialog({
      text: `Family member e-mail cannot be the same as the user's email(${familyInfo.value.email}).`,
    });

    return;
  }

  if (!familyInfo.value.familyId) {
    await createLovedOne(postData);
  } else {
    await updateLovedOne(postData);
  }
};

const resetForm = () => {
  familyInfo.value = {
    familyId: null,
    familyFirstName: '',
    familyLastName: '',
    familyPhoneNumber: '',
    isFamilyNoSms: true,
    familyEmail: '',
    familyRelation: '',
    otherRelationship: '',
    isAskedForLovedOneEmail: false,
  };

  isEmailValid.value = true;
};

watch(
  familyInfo,
  (value: any) => {
    const initialFamilyInfo = {
      familyId: newCustomerInfo.value.familyId,
      familyFirstName: newCustomerInfo.value.familyFirstName || '',
      familyLastName: newCustomerInfo.value.familyLastName || '',
      familyPhoneNumber: newCustomerInfo.value.familyPhoneNumber || '',
      isFamilyNoSms: newCustomerInfo.value.isFamilyNoSms || true,
      familyEmail: newCustomerInfo.value.familyEmail || '',
      familyRelation: newCustomerInfo.value.familyRelation || '',
      otherRelationship: newCustomerInfo.value.otherRelationship || '',
      isAskedForLovedOneEmail: newCustomerInfo.value.isAskedForLovedOneEmail ?? false,
    };

    const isFamilyInfoChanged = !util.compareJSON(value, initialFamilyInfo);

    emit('change-family', isFamilyInfoChanged);
  },
  { deep: true },
);

useEventListener('reset-registration-data', resetForm);

onMounted(() => {
  familyInfo.value = {
    familyId: newCustomerInfo.value.familyId,
    familyFirstName: newCustomerInfo.value.familyFirstName || '',
    familyLastName: newCustomerInfo.value.familyLastName || '',
    familyPhoneNumber: newCustomerInfo.value.familyPhoneNumber || '',
    isFamilyNoSms: newCustomerInfo.value.isFamilyNoSms || true,
    familyEmail: newCustomerInfo.value.familyEmail || '',
    familyRelation: newCustomerInfo.value.familyRelation || '',
    otherRelationship: newCustomerInfo.value.otherRelationship || '',
    isAskedForLovedOneEmail: newCustomerInfo.value.isAskedForLovedOneEmail || false,
  };
});
</script>
